import { ReactNode, useEffect } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useBootstrapStore } from '@/store/bootstrap.store';
import { fetchTenantConfigs } from '@/services/bootstrap/bootstrap.service';

interface BootstrapProviderProps {
  children: ReactNode;
}

export function BootstrapProvider({ children }: BootstrapProviderProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tenantId = useAuthStore((s) => s.tenantId);
  const status = useBootstrapStore((s) => s.status);
  const loadedTenantId = useBootstrapStore((s) => s.loadedTenantId);
  const setLoading = useBootstrapStore((s) => s.setLoading);
  const setLoaded = useBootstrapStore((s) => s.setLoaded);
  const setError = useBootstrapStore((s) => s.setError);
  const reset = useBootstrapStore((s) => s.reset);

  useEffect(() => {
    if (!isAuthenticated || !tenantId) {
      reset();
      return;
    }
    if (loadedTenantId === tenantId && (status === 'loaded' || status === 'loading')) {
      return;
    }
    let cancelled = false;
    setLoading(tenantId);
    const loadTenantConfigs = async (): Promise<void> => {
      try {
        const payload = await fetchTenantConfigs(tenantId);
        if (cancelled) {
          return;
        }
        setLoaded({ tenantId, ...payload });
      } catch (e) {
        if (cancelled) {
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadTenantConfigs();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tenantId, status, loadedTenantId, setLoading, setLoaded, setError, reset]);

  return <>{children}</>;
}
